// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Utility functions for building a directed hierarchy from Digital Twin
 * entities and relationships.
 *
 * The hierarchy represents the equipment tree (excluding Site entities)
 * with parent→child edges derived from containment (`hasPart`/`isPartOf`,
 * `containsEquipment`) and power flow (`suppliesPowerTo`/`suppliedBy`).
 */

import type { Entity, Relationship } from '../types/digitalTwin';
import { cleanEntityType, getEntityDisplayType, getTelemetryEntityIds } from './entityHelpers';
import { normalizeFlowRelationship } from './flowRelationships';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HierarchyNode {
  entity: Entity;
  displayType: string;
  children: HierarchyNode[];
  parent: HierarchyNode | null;
}

// ---------------------------------------------------------------------------
// Hierarchy builder
// ---------------------------------------------------------------------------

/**
 * Parent → child for one relationship, or null if it is not a hierarchy edge.
 *
 * Containment (`hasPart`/`isPartOf`, `containsEquipment`) and power flow
 * (`suppliesPowerTo`/`suppliedBy`, via `flowRelationships`). GA-11 publishes
 * `feeds`/`isFedBy` only for air and water, so they are not a hierarchy.
 */
const hierarchyEdge = (rel: Relationship): { parent: string; child: string } | null => {
  if (rel.type === 'hasPart' || rel.type === 'containsEquipment') {
    return { parent: rel.source, child: rel.target };
  }
  if (rel.type === 'isPartOf') return { parent: rel.target, child: rel.source };
  const flow = normalizeFlowRelationship(rel);
  return flow ? { parent: flow.source, child: flow.target } : null;
};

/** Containment before power flow: a breaker sits under its board, not under whatever feeds it. */
const isContainment = (rel: Relationship): boolean =>
  rel.type === 'hasPart' || rel.type === 'isPartOf' || rel.type === 'containsEquipment';

/**
 * Build a directed tree from entities and relationships.
 *
 * - Filters out Site entities (they are the container, not equipment).
 * - Filters out telemetry points (targets of hasTelemetryPoint/hasPoint
 *   edges) so they never surface as root-level "assets" — this is a
 *   structural check on the graph itself, independent of whatever
 *   type-name allow-list a caller may have already applied.
 * - Uses containment, then power flow, to establish parent→child edges
 *   (see `hierarchyEdge`). A child keeps the first parent it is given.
 * - Entities with no incoming hierarchy edges become root nodes.
 * - Falls back to a flat list if no relationships exist.
 */
export function buildHierarchy(entities: Entity[], relationships: Relationship[]): HierarchyNode[] {
  const telemetryIds = getTelemetryEntityIds(relationships);
  const equipment = entities.filter(
    e => cleanEntityType(e.type) !== 'Site' && !telemetryIds.has(e.id)
  );

  // Build a node map
  const nodeMap = new Map<string, HierarchyNode>();
  for (const entity of equipment) {
    nodeMap.set(entity.id, {
      entity,
      displayType: getEntityDisplayType(entity.type),
      children: [],
      parent: null,
    });
  }

  // Wire up parent→child edges from hierarchy relationships
  const ordered = [
    ...relationships.filter(isContainment),
    ...relationships.filter(r => !isContainment(r)),
  ];

  for (const rel of ordered) {
    const edge = hierarchyEdge(rel);
    if (!edge) continue;
    const parentNode = nodeMap.get(edge.parent);
    const childNode = nodeMap.get(edge.child);
    if (parentNode && childNode && childNode.parent === null) {
      let ancestor: HierarchyNode | null = parentNode;
      let isCycle = false;
      while (ancestor) {
        if (ancestor.entity.id === childNode.entity.id) {
          isCycle = true;
          break;
        }
        ancestor = ancestor.parent;
      }
      if (isCycle) continue;

      childNode.parent = parentNode;
      parentNode.children.push(childNode);
    }
  }

  const roots = Array.from(nodeMap.values()).filter(n => n.parent === null);

  return roots;
}

// ---------------------------------------------------------------------------
// Node lookup
// ---------------------------------------------------------------------------

/** Find a node by entity ID anywhere in a hierarchy tree.
 *
 * A `visited` set guards against infinite recursion if the graph
 * contains unexpected cycles.
 */
export function findNodeById(
  roots: HierarchyNode[],
  entityId: string,
  visited: Set<string> = new Set()
): HierarchyNode | null {
  for (const root of roots) {
    if (visited.has(root.entity.id)) continue;
    visited.add(root.entity.id);
    if (root.entity.id === entityId) return root;
    const found = findNodeById(root.children, entityId, visited);
    if (found) return found;
  }
  return null;
}

/** Collect ancestor IDs from a node up to the root (for auto-expanding tree paths). */
export function getAncestorIds(node: HierarchyNode): string[] {
  const ids: string[] = [];
  let current = node.parent;
  while (current) {
    ids.push(current.entity.id);
    current = current.parent;
  }
  return ids;
}
